import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  TrendingUp, Trophy, Calendar, Target, BarChart2, 
  Loader2, ChevronDown, ChevronUp 
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SeasonAveragesCard = ({ compact = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(!compact);
  const [posIdx, setPosIdx] = useState(0);

  useEffect(() => {
    fetchSeasonAverages();
  }, []);

  const fetchSeasonAverages = async () => {
    try {
      const response = await axios.get(`${API}/player/season-averages?months=12`);
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch season averages:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatStatKey = (key) => {
    return key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  const getGradeColor = (grade) => {
    const colors = {
      'A+': 'bg-green-500', 'A': 'bg-green-500', 'A-': 'bg-green-400',
      'B+': 'bg-blue-500', 'B': 'bg-blue-500', 'B-': 'bg-blue-400',
      'C+': 'bg-yellow-500', 'C': 'bg-yellow-500', 'C-': 'bg-yellow-400',
      'D': 'bg-orange-500', 'F': 'bg-red-500'
    };
    return colors[grade] || 'bg-slate-500';
  };

  if (loading) {
    return (
      <Card className="bg-slate-800/90 border-slate-700">
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-amber-400" />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.total_games === 0) {
    return (
      <Card className="bg-slate-800/90 border-slate-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <BarChart2 className="w-5 h-5 text-amber-400" />
            Season Averages
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-slate-400 text-center py-4">
            Complete some game sessions to see your season averages
          </p>
        </CardContent>
      </Card>
    );
  }

  // Multi-position athletes get one tab per (sport, position) they genuinely play.
  const positions = data.positions || [];
  const view = positions[posIdx] || {
    position: data.primary_position,
    games: data.primary_position_games,
    averages: data.averages,
    stat_labels: data.stat_labels,
  };

  // Get top stats (highest averages) for the selected position
  const sortedStats = Object.entries(view.averages || {})
    .filter(([_, v]) => v.average_per_game > 0)
    .sort((a, b) => b[1].average_per_game - a[1].average_per_game);
  
  const topStats = sortedStats.slice(0, compact ? 4 : 8);

  return (
    <Card className="bg-slate-800/90 border-slate-700">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <BarChart2 className="w-5 h-5 text-amber-400" />
            Season Averages
          </CardTitle>
          <Badge className="bg-slate-700 text-slate-300">
            {data.total_games} Games
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Position switcher for multi-position athletes */}
        {positions.length > 1 && (
          <div className="flex flex-wrap gap-2" data-testid="season-position-tabs">
            {positions.map((p, i) => (
              <button
                key={`${p.sport}-${p.position}`}
                onClick={() => setPosIdx(i)}
                data-testid={`season-position-tab-${p.position}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold capitalize transition-colors ${
                  i === posIdx
                    ? 'bg-amber-500 text-slate-900'
                    : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                }`}
              >
                {p.position?.replace(/_/g, ' ')}
                <span className={`ml-1 ${i === posIdx ? 'text-slate-800' : 'text-slate-400'}`}>
                  ({p.games})
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Summary Row */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-slate-700/50 rounded-lg p-3 text-center">
            <Calendar className="w-4 h-4 text-slate-400 mx-auto mb-1" />
            <p className="text-xl font-bold text-white" data-testid="season-position-games">{view.games}</p>
            <p className="text-xs text-slate-400">Games</p>
          </div>
          <div className="bg-slate-700/50 rounded-lg p-3 text-center">
            <Target className="w-4 h-4 text-slate-400 mx-auto mb-1" />
            <p className="text-xl font-bold text-white capitalize" data-testid="season-position-name">
              {view.position?.replace(/_/g, ' ')}
            </p>
            <p className="text-xs text-slate-400">Position</p>
          </div>
          <div className="bg-slate-700/50 rounded-lg p-3 text-center">
            <Trophy className="w-4 h-4 text-slate-400 mx-auto mb-1" />
            <div className="flex justify-center gap-1 flex-wrap">
              {Object.entries(data.grade_distribution || {}).slice(0, 3).map(([grade, count]) => (
                <Badge key={grade} className={`${getGradeColor(grade)} text-white text-xs`}>
                  {grade}: {count}
                </Badge>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-1">Grades</p>
          </div>
        </div>

        {/* Toggle for expanded view */}
        {compact && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full flex items-center justify-center gap-1 text-slate-400 hover:text-white text-sm py-1"
          >
            {expanded ? 'Show Less' : 'Show More'}
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        )}

        {/* Stats Grid */}
        {expanded && (
          <div className="space-y-2">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Per-Game Averages</p>
            <div className="grid grid-cols-2 gap-2">
              {topStats.map(([key, value]) => (
                <div 
                  key={key}
                  className="bg-slate-700/30 rounded-lg p-3 flex items-center justify-between"
                >
                  <span className="text-slate-300 text-sm truncate pr-2">
                    {view.stat_labels?.[key] || formatStatKey(key)}
                  </span>
                  <span className="text-white font-bold">
                    {value.average_per_game}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Show all stats in expanded mode */}
        {expanded && sortedStats.length > 8 && (
          <details className="text-sm">
            <summary className="text-slate-400 cursor-pointer hover:text-white">
              View all {sortedStats.length} stats
            </summary>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {sortedStats.slice(8).map(([key, value]) => (
                <div 
                  key={key}
                  className="bg-slate-700/20 rounded-lg p-2 flex items-center justify-between"
                >
                  <span className="text-slate-400 text-xs truncate pr-2">
                    {view.stat_labels?.[key] || formatStatKey(key)}
                  </span>
                  <span className="text-slate-300 font-medium text-sm">
                    {value.average_per_game}
                  </span>
                </div>
              ))}
            </div>
          </details>
        )}
      </CardContent>
    </Card>
  );
};

export default SeasonAveragesCard;
